import { SecondFromMidnight, TimeSlot, WorkDateType } from "@cervval.js/calendars/src/calendars.definition";
import { WorkCalendarHelper } from "@cervval.js/calendars/src/calendars.helper";
import { buildCalendars } from "@cervval.js/calendars/src/calendars.tools";
import { ActionService } from "@cervval.js/common-action/src/services/action.service";
import { SqlDbDefinition } from "@cervval.js/common-sql-core";
import { setProp } from "@cervval.js/common/src/tools/any.tools";
import { objectKeys } from "@cervval.js/common/src/tools/object.tools";
import { isPromise } from "@cervval.js/common/src/tools/promise.tools";
import { SECONDS_IN_ONE_HOUR } from "@cervval.js/common/src/tools/time.tools";
import { add, NOW, UnixTimestamp } from "@cervval.js/common/src/tools/timestamps.tools";
import { DAYS } from "@cervval.js/common/src/tools/timestamps.tools.definitions";
import { anyReallyAny } from "@cervval.js/common/tools";
import { BaseElementTypes } from "@ehealth_tools/common/src//data/dto/base.dto";
import { ID } from "@ehealth_tools/common/src/data/propertyTypes/property.types";
import { TypedKey } from "@ehealth_tools/common/src/types/types.definition";
import { ResourceUsageCache } from "@zurvan.js/resources/src/usages/resourceUsage.cache";
import { RelativeUsageOptions } from "@zurvan.js/resources/src/usages/usage.cache";
import { BookingCalendar, BookingCommonUser, BookingCompany, BookingException, BookingProduct, BookingProfessional, BookingProfessionalCompany, BookingSession, BookingSharing, BookingUser, BookingUserSession, BookingUserSpecialty, BookingWeek } from "../data/booking.dto";
import { isProfessional } from "../data/booking.tools";
import { _SAFE_SHARING } from "../data/booking.types";
import { DeleteUserSessionReturnType } from "../routes/authBooking.routes";
import { mergeTimeSlots } from "../tools/calendar.tools";
import { ceilDateByMinute } from "../tools/date.tools";

export const BOOKING_SERVICE = "BookingService";


type BookingTables<ElementTypes> = {
    bookingSessions: TypedKey<ElementTypes, BookingSession>,
    bookingUsers: TypedKey<ElementTypes, BookingCommonUser>,
    bookingCompanies: TypedKey<ElementTypes, BookingCompany>,
    bookingCalendars: TypedKey<ElementTypes, BookingCalendar>,
    bookingWeeks: TypedKey<ElementTypes, BookingWeek>,
    bookingExceptions: TypedKey<ElementTypes, BookingException>,
    bookingProducts: TypedKey<ElementTypes, BookingProduct>,
    bookingUserSessions: TypedKey<ElementTypes, BookingUserSession>,
    bookingUserSpecialty: TypedKey<ElementTypes, BookingUserSpecialty>,
    bookingSharing: TypedKey<ElementTypes, BookingSharing>,
    bookingProfessionalCompany: TypedKey<ElementTypes, BookingProfessionalCompany>
};
export interface CommonBookingServiceParams<ElementTypes, AppUser extends BookingCommonUser, AppProfessionalCompany extends BookingProfessionalCompany, IsPromise extends boolean> {
    getData: <T extends keyof ElementTypes>(table: keyof ElementTypes) => IsPromise extends true ? Promise<ElementTypes[T][]> : ElementTypes[T][];
    getById: <T extends keyof ElementTypes>(table: keyof ElementTypes, id: ID) => IsPromise extends true ? Promise<ElementTypes[T]> : ElementTypes[T];
    tables: BookingTables<ElementTypes>;
    dbDefinitions: SqlDbDefinition<keyof ElementTypes, unknown>;
    isMember: (pro: BookingProfessional & AppUser) => boolean;
    isProfessionalCompanyRelationShip: (proCompany: AppProfessionalCompany) => boolean;
}

interface Initializers {
    product: () => Promise<Partial<BookingProduct>>;
}
export interface CommonBookingServiceOptions {
    initializers?: Partial<Initializers>;
}

const defaultCommonBookingServiceOptions: CommonBookingServiceOptions = {
    //
};

/**
 * Service for booking
 */
export abstract class AbstractCommonBookingService<ElementTypes extends BaseElementTypes, AppUser extends BookingCommonUser, AppProfessionalCompany extends BookingProfessionalCompany, IsPromise extends boolean> {

    public abstract actionService: ActionService<ElementTypes, anyReallyAny>;


    constructor(protected _params: CommonBookingServiceParams<ElementTypes, AppUser, AppProfessionalCompany, IsPromise>, protected _options?: CommonBookingServiceOptions) {
        this._options = { ...defaultCommonBookingServiceOptions, ...this._options };
    }

    /** */
    public async updateValues<Table extends BookingTables<ElementTypes> & keyof ElementTypes & string>(table: Table, dto: ElementTypes[Table], values: Partial<ElementTypes[Table]>): Promise<void> {
        await this.actionService.encapsulateInAction(action => {
            for (const prop of objectKeys(values)) {
                setProp(dto, prop as string, Number.NaN); // Something stupid only to force updateValues to detect the change
            }
            action.updateValuesIfNeeded(table, dto, values);
        }, { logMessage: `Update ${table}` });
    }

    /** */
    public initializeDTO<Type extends keyof Initializers>(type: Type): ReturnType<Initializers[Type]> | null {
        const initializer = this._options?.initializers?.[type];
        if (initializer == null) { return null; }
        return initializer() as ReturnType<Initializers[Type]>;
    }

    /**
     * Retrieve a company in which given professional user is affected
     * @param pro Professional user
     * @returns ```BookingCompany``` object or undefined if the professional user has no current affectations
     * FIXME Will not handle a professional affected on several structures => it will only return the company associated to the first affectation found for user
     */
    public getCompany(pro: BookingProfessional & AppUser): IsPromise extends true ? Promise<BookingCompany> : BookingCompany | undefined {
        const pRelationShips = this.getData<AppProfessionalCompany>("bookingProfessionalCompany");
        const isProfessionalOfCompany: (v: AppProfessionalCompany) => boolean =
            (v: AppProfessionalCompany) => this._params.isProfessionalCompanyRelationShip(v) && v.userId === pro.id;
        if (isPromise(pRelationShips)) {
            return pRelationShips.then(async relationShips => {
                const proRelationShipCompanyIds = new Set(relationShips.filter(isProfessionalOfCompany).map(v => v.companyId));
                const companies = await this.getData<BookingCompany>("bookingCompanies");
                const company = companies.find(v => proRelationShipCompanyIds.has(v.id));
                return company;
            }) as ReturnType<typeof this.getCompany>;
        } else {
            const proRelationShipCompanyIds = new Set(pRelationShips.filter(isProfessionalOfCompany).map(v => v.companyId));
            const companies = this.getData<BookingCompany>("bookingCompanies") as BookingCompany[];
            const company = companies.find(v => proRelationShipCompanyIds.has(v.id));
            return company as ReturnType<typeof this.getCompany>;
        }
    }

    /** */
    public getById<T>(table: keyof BookingTables<ElementTypes>, id: ID): IsPromise extends true ? Promise<T> : T {
        return this._params.getById(this.tables[table], id) as IsPromise extends true ? Promise<T> : T;
    }

    /** */
    public getData<T>(table: keyof BookingTables<ElementTypes>): IsPromise extends true ? Promise<T[]> : T[] {
        return this._params.getData(this.tables[table]) as IsPromise extends true ? Promise<T[]> : T[];
    }

    /** Get user by Id */
    public getUserById(id: ID): IsPromise extends true ? Promise<BookingCommonUser> : BookingCommonUser {
        return this.getById<BookingUser>("bookingUsers", id);
    }

    /** */
    public getUsers(): IsPromise extends true ? Promise<BookingCommonUser[]> : BookingCommonUser[] {
        return this.getData<BookingCommonUser>("bookingUsers");
    }

    /** */
    public getProfessional(options?: { onlyAdvertising?: boolean }): IsPromise extends true ? Promise<BookingProfessional[]> : BookingProfessional[] {
        const p = this.getUsers();
        if (isPromise(p)) {
            return p.then(dataList => {
                return dataList.filter(v => isProfessional(v) && (!options?.onlyAdvertising || v.advertisement !== _SAFE_SHARING.NONE)) as BookingProfessional[];
            }) as IsPromise extends true ? Promise<BookingProfessional[]> : BookingProfessional[];
        } else {
            return p.filter(v => isProfessional(v) && (!options?.onlyAdvertising || v.advertisement !== _SAFE_SHARING.NONE)) as IsPromise extends true ? Promise<BookingProfessional[]> : BookingProfessional[];
        }
    }

    /** */
    public getProducts(): IsPromise extends true ? Promise<BookingProduct[]> : BookingProduct[] {
        return this.getData<BookingProduct>("bookingProducts");
    }

    /** */
    public get tables(): BookingTables<ElementTypes> {
        return this._params.tables;
    }

    /**
     * Retrieve the speciality of a professional
     * @param user 
     * @returns 
     */
    public getProfessionalSpecialty(user: BookingProfessional): IsPromise extends true ? Promise<BookingUserSpecialty | undefined> : BookingUserSpecialty | undefined {
        const pSpecialities = this.getData<BookingUserSpecialty>("bookingUserSpecialty");
        if (isPromise(pSpecialities)) {
            return pSpecialities.then(specialties => {
                return specialties.filter((val) => val.userId === user.id)?.[0];
            }) as ReturnType<typeof this.getProfessionalSpecialty>;
        } else {
            return pSpecialities.filter((val) => val.userId === user.id)?.[0] as ReturnType<typeof this.getProfessionalSpecialty>;
        }
    }

    /** */
    public get dbDefinitions(): SqlDbDefinition<keyof ElementTypes, unknown> {
        return this._params.dbDefinitions;
    }

    /** Propose a slot for the given product on given professional */
    public getNextSlot(product: BookingProduct, professionalId: ID, from: UnixTimestamp = NOW()): IsPromise extends true ? Promise<UnixTimestamp> : UnixTimestamp {

        const pRuc = this.getRUC(professionalId);
        if (isPromise(pRuc)) {
            return pRuc.then(async ruc => {
                const workCalendarHelper = await this.getUserWorkCalendarHelper(professionalId);

                return this._getNextSlotImpl(ruc, workCalendarHelper, professionalId, product, from);

            }) as ReturnType<typeof this.getNextSlot>;
        } else {
            const workCalendarHelper = this.getUserWorkCalendarHelper(professionalId) as WorkCalendarHelper | null;
            return this._getNextSlotImpl(pRuc, workCalendarHelper, professionalId, product, from);
        }


    }

    /** */
    private _getNextSlotImpl(ruc: ResourceUsageCache<ID, BookingSession>, workCalendarHelper: WorkCalendarHelper | null, professionalId: ID, product: BookingProduct, from: UnixTimestamp): IsPromise extends true ? Promise<UnixTimestamp> : UnixTimestamp {
        let date = this.assessResourceOnRUC(ruc, professionalId, { duration: { second: product.duration }, lag: { second: 0 }, quantity: 1 }, from);

        if (workCalendarHelper) {
            const cDate = workCalendarHelper.getWorkableDate(date ?? from, WorkDateType.START).uts;
            if (date === cDate) {
                date = cDate;
            } else {
                return this.getNextSlot(product, professionalId, cDate);
            }
        } else {
            date = date ?? from;
        }
        return ceilDateByMinute(date, 5) as ReturnType<typeof this._getNextSlotImpl>;
    }

    /** Get resource usage cache. If no user is provided, build a RUC for every available sessions */
    public getRUC(userId?: ID): IsPromise extends true ? Promise<ResourceUsageCache<ID, BookingSession>> : ResourceUsageCache<ID, BookingSession> {
        const sessions = this.getData<BookingSession>("bookingSessions");
        if (isPromise(sessions)) {
            return sessions.then(s => this._getRUCImpl(s, userId)) as IsPromise extends true ? Promise<ResourceUsageCache<ID, BookingSession>> : ResourceUsageCache<ID, BookingSession>;
        } else {
            return this._getRUCImpl(sessions, userId) as IsPromise extends true ? Promise<ResourceUsageCache<ID, BookingSession>> : ResourceUsageCache<ID, BookingSession>;
        }
    }

    /** Sync impl */
    private _getRUCImpl(sessions: BookingSession[], userId?: ID): ResourceUsageCache<ID, BookingSession> {
        const ruc = new ResourceUsageCache<ID, BookingSession>();
        for (const session of sessions) {
            if (userId && userId !== session.professionalId) { continue; }
            ruc.use(session.professionalId, 1, {
                start: session.start,
                finish: add(session.start, { second: session.duration }).uts,
                data: session,
                skipRecomputeQuantities: true
            });
        }
        ruc.recompute();
        return ruc;
    }


    /** */
    public assessResourceOnRUC(ruc: ResourceUsageCache<ID, BookingSession>, ressourceId: ID, assay: RelativeUsageOptions<BookingSession>, from: UnixTimestamp): UnixTimestamp | null {
        const m: Map<ID, RelativeUsageOptions<BookingSession>[]> = new Map();
        m.set(ressourceId, [assay]);
        return ruc.getNextAvailability(from, m);
    }


    /** */
    public getUserWorkCalendarHelper(userId: ID): IsPromise extends true ? Promise<WorkCalendarHelper | null> : WorkCalendarHelper | null {
        // Data
        const pUser = this.getById<BookingUser>("bookingUsers", userId);
        if (isPromise(pUser)) {
            return pUser.then(async user => {
                const calendarId = user.calendarId;
                const [calendars, weeks, exceptions] = await Promise.all([
                    this.getData<BookingCalendar>("bookingCalendars"),
                    this.getData<BookingWeek>("bookingWeeks"),
                    this.getData<BookingException>("bookingExceptions")
                ]);
                const filterdCalendars = calendars.filter(v => v.id === calendarId);
                return this._getUserWorkCalendarHelperImpl(filterdCalendars, weeks, exceptions);
            }) as ReturnType<typeof this.getUserWorkCalendarHelper>;
        } else {
            const calendarId = pUser.calendarId;
            const calendars = (this.getData<BookingCalendar>("bookingCalendars") as BookingCalendar[]).filter(v => v.id === calendarId);
            const weeks = this.getData<BookingWeek>("bookingWeeks") as BookingWeek[];
            const exceptions = this.getData<BookingException>("bookingExceptions") as BookingException[];
            return this._getUserWorkCalendarHelperImpl(calendars, weeks, exceptions) as ReturnType<typeof this.getUserWorkCalendarHelper>;
        }


    }

    private _getUserWorkCalendarHelperImpl(calendars: BookingCalendar[], weeks: BookingWeek[], exceptions: BookingException[]): WorkCalendarHelper | null {
        // Should be only one calendar
        const workCalendars = buildCalendars({
            calendars,
            calendarWeekIdKey: "defaultWeekId",
            exceptionCalendarIdKey: "calendarId",
            exceptionFinishKey: "finish",
            exceptions,
            exceptionStartKey: "start",
            exceptionWeekIdKey: "weekId",
            weeks
        });
        const workCalendar = workCalendars?.[0];
        if (workCalendar == null) {
            return null;
        }

        return new WorkCalendarHelper(workCalendar);
    }

    public abstract book(p: {
        professionalId: ID,
        customerIds: ID[],
        productId: ID,
        date: UnixTimestamp
    },
        options?: {
            price?: number,
            duration?: number,
            description?: string
        }): Promise<{ session: BookingSession, userSessions: BookingUserSession[] }>

    /** */
    public async setTimeSlots(timeslots: TimeSlot[], week: BookingWeek, days: DAYS[]): Promise<void> {
        const mTimeSlots = mergeTimeSlots(timeslots);
        const currentWeek = { ...week.week };
        for (const d of days) {
            currentWeek[d] = { timeslots: mTimeSlots };
        }
        await this.actionService.encapsulateInAction(action => {
            action.updateValueIfNeeded(this.tables.bookingWeeks, week as anyReallyAny, "week", currentWeek);
        }, { logMessage: "Save new timeslots" });
    }

    /** */
    public static get defaultTimeSlots(): TimeSlot[] {
        return [
            {
                start: 8 * SECONDS_IN_ONE_HOUR as SecondFromMidnight,
                finish: 12 * SECONDS_IN_ONE_HOUR as SecondFromMidnight
            },
            {
                start: 14 * SECONDS_IN_ONE_HOUR as SecondFromMidnight,
                finish: 18 * SECONDS_IN_ONE_HOUR as SecondFromMidnight
            }
        ];
    }

    /** Default data */
    public static get defaultWeek(): BookingWeek {
        return {
            id: -1,
            week: {
                [DAYS.MONDAY]: { timeslots: AbstractCommonBookingService.defaultTimeSlots },
                [DAYS.TUESDAY]: { timeslots: AbstractCommonBookingService.defaultTimeSlots },
                [DAYS.WEDNESDAY]: { timeslots: AbstractCommonBookingService.defaultTimeSlots },
                [DAYS.THURSDAY]: { timeslots: AbstractCommonBookingService.defaultTimeSlots },
                [DAYS.FRIDAY]: { timeslots: AbstractCommonBookingService.defaultTimeSlots },
                [DAYS.SATURDAY]: { timeslots: [] },
                [DAYS.SUNDAY]: { timeslots: [] }
            }
        };
    }

    /** Tell if the given member is part of the platform or just added from annuaire */
    public isMember(professional: BookingProfessional & AppUser): boolean {
        return this._params.isMember(professional);
    }

    /** */
    public getSessionData(sessionId: ID): IsPromise extends true ? Promise<{ session: BookingSession, userSessions: BookingUserSession[] }> : { session: BookingSession, userSessions: BookingUserSession[] } {
        const pSession = this.getById<BookingSession>("bookingSessions", sessionId);
        if (isPromise(pSession)) {
            return pSession.then(async session => {
                const userSessions = (await this.getData<BookingUserSession>("bookingUserSessions")).filter(v => v.sessionId === session.id);
                return { session, userSessions };
            }) as ReturnType<typeof this.getSessionData>;
        } else {
            const userSessions = (this.getData<BookingUserSession>("bookingUserSessions") as BookingUserSession[]).filter(v => v.sessionId === pSession.id);
            return { session: pSession, userSessions } as ReturnType<typeof this.getSessionData>;
        }
    }

    public abstract deleteUserSession(userSessionId: ID): Promise<DeleteUserSessionReturnType>;
}